<template>
  <!-- FT-102 (REQ-02/03/07/08): блок «Допы» в брони. Два режима:
       draft (NewBooking: бронь ещё не создана — локальный выбор, строки постятся
       родителем после создания через postDraft) и live (BookingCard: прямой CRUD
       строк с перепроверкой остатка на сервере). Блок скрыт при пустом каталоге
       (REQ-06 empty-state). -->
  <v-card v-if="addons.length > 0" outlined class="mt-2 pa-3">
    <div class="d-flex align-center mb-1">
      <v-icon small class="mr-2">mdi-package-variant-closed</v-icon>
      <h4 class="mb-0">{{ $t("booking_addons.title") }}</h4>
      <v-spacer></v-spacer>
      <span v-if="!datesChosen" class="grey--text caption">{{ $t("booking_addons.pick_dates_hint") }}</span>
    </div>
    <div v-if="live && marketplace" class="orange--text text--darken-1 caption mb-1">
      <v-icon x-small color="orange darken-1" class="mr-1">mdi-alert-outline</v-icon>{{ $t("booking_addons.marketplace_hint") }}
    </div>
    <div v-for="addon in addonsWithPending" :key="addon.id" class="d-flex align-center flex-wrap py-1"
      style="border-top: 1px solid rgba(128, 128, 128, 0.15)">
      <v-avatar size="24" class="mr-2"
        :color="addon.image_url ? undefined : ($vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-3')">
        <img v-if="addon.image_url" :src="addon.image_url" />
        <v-icon v-else x-small :color="$vuetify.theme.dark ? 'grey lighten-1' : 'grey darken-1'">
          mdi-package-variant-closed
        </v-icon>
      </v-avatar>
      <div class="mr-auto">
        {{ addon.name }}
        <div class="grey--text caption">
          {{ effectivePrice(addon) }}
          {{ addon.pricing_mode === "per_booking" ? $t("booking_addons.per_booking_short") : $t("booking_addons.per_unit_short") }}
          <v-icon v-if="addon.price_overridden" x-small color="warning" class="ml-1"
            :disabled="disabled || busy"
            @click="resetToCatalog(addon, 'price')" v-tooltip="{
              content: overriddenHint(addon, 'price'),
              placement: 'bottom-center',
              classes: ['info'],
              targetClasses: ['it-has-a-tooltip'],
            }">mdi-pencil-outline</v-icon>
          <!-- Ревью round 4 (п.3): договорной залог 0 — легальное значение
               (позицию отдали без залога), гейт по effectiveDeposit > 0
               прятал вместе с пустой строкой и саму пометку override. -->
          <template v-if="effectiveDeposit(addon) > 0 || addon.deposit_overridden">
            · {{ $t("booking_addons.deposit_short") }} {{ effectiveDeposit(addon) }}
            <v-icon v-if="addon.deposit_overridden" x-small color="warning" class="ml-1"
              :disabled="disabled || busy"
              @click="resetToCatalog(addon, 'deposit')" v-tooltip="{
                content: overriddenHint(addon, 'deposit'),
                placement: 'bottom-center',
                classes: ['info'],
                targetClasses: ['it-has-a-tooltip'],
              }">mdi-pencil-outline</v-icon>
          </template>
          <template v-if="!addon.track_availability"> · {{ $t("booking_addons.service") }}</template>
        </div>
      </div>
      <v-text-field :value="effectivePrice(addon)" type="number" min="0" dense hide-details outlined
        :disabled="disabled || busy" :label="$t('booking_addons.price_label')"
        style="max-width: 110px; flex: 0 0 110px" class="ml-2"
        @change="applyPricing(addon, { price: $event })"></v-text-field>
      <v-text-field :value="effectiveDeposit(addon)" type="number" min="0" dense hide-details outlined
        :disabled="disabled || busy" :label="$t('booking_addons.deposit_label')"
        style="max-width: 110px; flex: 0 0 110px" class="ml-2"
        @change="applyPricing(addon, { deposit: $event })"></v-text-field>
      <v-text-field :key="'qty-' + addon.id + '-' + (declineEpoch[addon.id] || 0)"
        :value="quantityFor(addon)" type="number" min="0" dense hide-details outlined
        :disabled="disabled || busy || (addon.track_availability && !datesChosen)"
        :suffix="stockSuffix(addon)" :error="addon.track_availability && availableFor(addon) === 0"
        style="max-width: 130px; flex: 0 0 130px" class="ml-3"
        @change="onQuantityChange(addon, $event)"></v-text-field>
    </div>
    <div v-if="addonsCost > 0" class="text-right mt-2">
      <b>{{ $t("booking_addons.total") }}: {{ addonsCost }}</b>
      <span v-if="addonsDeposit > 0" class="grey--text ml-2">
        {{ $t("booking_addons.deposit_total") }}: {{ addonsDeposit }}
      </span>
    </div>
  </v-card>
</template>

<script>
import moment from "moment";
import {
  addonsCost,
  addonsDeposit,
  effectivePrice,
  effectiveDeposit,
  mergePendingPricing,
  buildCreatePayload,
  normalizeMoney,
} from "@/components/dashboard/bookings/addonsMath";

export default {
  name: "BookingAddonsPanel",
  props: {
    // live: id существующей брони; draft: null (NewBooking до сохранения)
    bookingId: { type: Number, default: null },
    startDate: { type: String, default: null }, // "DD-MM-YYYY HH:mm"
    endDate: { type: String, default: null },
    units: { type: Number, default: 1 }, // единица тарификации брони (дни/часы/…)
    disabled: { type: Boolean, default: false },
    // Бронь пришла с маркетплейса (vseprokaty_id): каталожные строки не
    // транслируются в детализацию на сайте агрегатора — предупреждаем менеджера
    // (решение USER 2026-08-05; полная трансляция строк — FT-086)
    marketplace: { type: Boolean, default: false },
  },
  data() {
    return {
      addons: [],
      selection: {}, // draft: store_product_id -> quantity
      // Ревью Task 7 (Important): договорная цена/залог, введённые ДО того как
      // у позиции появилась строка — единое хранилище для live (строки ещё
      // нет, line_id отсутствует) и draft (брони ещё нет вовсе). Ключ —
      // store_product_id, значение — { price, deposit } (только заданные поля).
      pendingPricing: {},
      // Ревью round 4 (п.1, фикс): счётчик отказов от подтверждения обнуления
      // количества — накручивается для addon.id при каждом отказе и идёт в
      // :key поля количества (см. template), чтобы форсировать его
      // пересоздание. Подробности — applyLiveChange.
      declineEpoch: {},
      busy: false,
    };
  },
  computed: {
    authHeaders() {
      return { headers: { Authorization: this.$store.getters.getAuthToken } };
    },
    live() {
      return this.bookingId !== null;
    },
    // Даты выбраны и распознаны — иначе остаток неизвестен (показываем подсказку)
    datesChosen() {
      return Boolean(this.startDate && this.endDate);
    },
    // Ревью Task 7: addons с наложенной отложенной ценой/залогом (пока строки
    // нет) — единый источник и для рендера строк, и для сумм панели, чтобы
    // экран не показывал каталожную цену, когда сохранится договорная.
    addonsWithPending() {
      return this.addons.map((addon) => mergePendingPricing(addon, this.pendingPricing[addon.id]));
    },
    // DEC-01: формулы в addonsMath.js (юнит-тестируются напрямую)
    addonsCost() {
      return addonsCost(this.addonsWithPending, this.selection, this.units);
    },
    addonsDeposit() {
      return addonsDeposit(this.addonsWithPending, this.selection);
    },
  },
  watch: {
    startDate() {
      this.fetchAddons();
    },
    endDate() {
      this.fetchAddons();
    },
    addonsCost(value, old) {
      this.$emit("addons-cost-changed", { value, delta: value - old });
    },
    addonsDeposit(value, old) {
      // REQ-08: подсказка залога — ТОЛЬКО в draft-режиме (NewBooking), где
      // selection меняет пользователь. В live-режиме сохранённый deposit уже
      // содержит залог допов — эмит при гидрации давал ДВОЙНОЙ префилл
      // (+50 на каждую загрузку карточки, UAT 2026-08-04); там дельту шлёт
      // applyLiveChange по факту пользовательского изменения.
      if (!this.live) {
        this.$emit("addons-deposit-changed", { value, delta: value - old });
      }
    },
  },
  created() {
    this.fetchAddons();
  },
  beforeDestroy() {
    // Страховка (только draft): при уничтожении панели вклад в залог
    // возвращается; в live сохранённый deposit панели не принадлежит
    if (!this.live && this.addonsDeposit > 0) {
      this.$emit("addons-deposit-changed", { value: 0, delta: -this.addonsDeposit });
    }
  },
  methods: {
    fetchAddons() {
      const params = {};
      if (this.startDate) params.start_date = this.startDate;
      if (this.endDate) params.end_date = this.endDate;
      if (this.bookingId) params.booking_id = this.bookingId;
      return this.axios
        .get(`/api/v1/bookable_addons`, { params, ...this.authHeaders })
        .then((response) => {
          this.addons = response.data.addons || [];
          // Ревью round 4 (п.7): строка могла появиться мимо панели (вторая
          // вкладка, другой менеджер) — как только у позиции есть line_id,
          // отложенное значение уже никто не отправит на сервер, а
          // addonsWithPending продолжал бы показывать локальную цену поверх
          // фактической серверной ("на экране 80, на сервере 100").
          this.addons.forEach((addon) => {
            if (addon.line_id && this.pendingPricing[addon.id] !== undefined) {
              this.$delete(this.pendingPricing, addon.id);
            }
          });
          this.$emit("addons-loaded", this.addons.length);
          this.$emit("lines-changed", this.addons
            .filter((a) => a.in_booking_quantity)
            .map((a) => ({ name: a.name, quantity: a.in_booking_quantity })));
          if (this.live) {
            const selection = {};
            this.addons.forEach((addon) => {
              if (addon.in_booking_quantity) selection[addon.id] = addon.in_booking_quantity;
            });
            this.selection = selection;
          }
        })
        .catch((error) => this.notifyError(error, this.$t("booking_addons.load_error")));
    },
    quantityFor(addon) {
      return this.selection[addon.id] || 0;
    },
    effectivePrice,
    effectiveDeposit,
    overriddenHint(addon, field) {
      const catalog = field === "price" ? addon.rental_price : addon.deposit;
      const who = addon.pricing_changed_by_name;
      // Ревью round 4 (п.8): сервер отдаёт pricing_changed_at — согласованный
      // формат подсказки обещает дату правки, а её не было.
      const when = addon.pricing_changed_at ? moment(addon.pricing_changed_at).format("DD.MM.YYYY HH:mm") : null;
      const parts = [this.$t("booking_addons.overridden"), `${this.$t("booking_addons.in_catalog")} ${catalog}`];
      if (who) parts.push(who);
      if (when) parts.push(when);
      parts.push(this.$t("booking_addons.apply_catalog"));
      return parts.join(" · ");
    },
    resetToCatalog(addon, field) {
      // Ревью round 4 (п.2): v-icon не блокирует @click нативным disabled
      // (это не форм-контрол) — карандаш обходил тумблер «Изменить стоимость
      // услуг» (:disabled на панели) и не давал гарда от двойного клика,
      // из-за чего второй PATCH считал дельту залога от устаревшего
      // previousDeposit. Гейт — то же условие, что и у соседних полей.
      if (this.disabled || this.busy) return;
      const payload = field === "price" ? { price: addon.rental_price } : { deposit: addon.deposit };
      return this.applyPricing(addon, payload);
    },
    // Ревью Task 7: единое хранилище отложенной цены/залога (см. data()) —
    // используется и для live-строки без line_id, и для draft.
    storePendingPricing(addonId, payload) {
      const existing = this.pendingPricing[addonId] || {};
      this.$set(this.pendingPricing, addonId, { ...existing, ...payload });
    },
    // Правка денег без количества: сервер сохранит цену/залог и вернёт
    // booking_total (REQ-07 — total ведёт сервер)
    async applyPricing(addon, rawPayload) {
      // Ревью Task 7 (Minor + round-3 Important): нормализуем ввод, но
      // очищенное поле ("" / null) — это "не менять" (money_param на
      // сервере), а не команда обнулить цену; normalizeMoney возвращает
      // undefined для такого ввода, и такой ключ вообще не попадает ни в
      // запрос, ни в отложенное хранилище. Мусор/отрицательное — осмысленное
      // "сделать бесплатным", коэрцируется в 0.
      const payload = {};
      if (rawPayload.price !== undefined) {
        const price = normalizeMoney(rawPayload.price);
        if (price !== undefined) payload.price = price;
      }
      if (rawPayload.deposit !== undefined) {
        const deposit = normalizeMoney(rawPayload.deposit);
        if (deposit !== undefined) payload.deposit = deposit;
      }
      if (Object.keys(payload).length === 0) return; // поле очищено — ничего менять не нужно

      if (!this.live) {
        // Draft (NewBooking): бронь ещё не создана — сохраняем в pendingPricing
        // (тот же объект, что и для live-строки без line_id) и сообщаем
        // родителю; отправка в postDraft — отдельная задача плана.
        this.storePendingPricing(addon.id, payload);
        this.$emit("draft-pricing-changed", { addonId: addon.id, ...payload });
        return;
      }
      if (!addon.line_id) {
        // Ревью Task 7 (Important, было): строки ещё нет — PATCH некуда
        // слать. Раньше это просто терялось (return без сохранения) — теперь
        // цена/залог откладываются в pendingPricing, а applyLiveChange
        // подмешает их в тело POST через buildCreatePayload, когда менеджер
        // введёт количество и строка будет создана.
        this.storePendingPricing(addon.id, payload);
        return;
      }
      this.busy = true;
      const previousDeposit = effectiveDeposit(addon);
      try {
        const response = await this.axios.patch(
          `/api/v1/bookings/${this.bookingId}/booking_store_products/${addon.line_id}`,
          payload, this.authHeaders);
        // Ревью round 4 (п.9): applyLiveChange гейтит эмит тем же событием
        // строго по `addons_cost !== undefined` — здесь гейт был мягче
        // (`response.data` truthy), приводим к единому строгому варианту.
        if (response && response.data && response.data.addons_cost !== undefined) {
          this.$emit("server-addons-cost", {
            addons_cost: response.data.addons_cost,
            booking_total: response.data.booking_total,
          });
        }
        if (response && response.data && payload.deposit !== undefined) {
          const qty = this.quantityFor(addon);
          const delta = (Number(response.data.deposit) - previousDeposit) * qty;
          if (delta !== 0) this.$emit("addons-deposit-changed", { value: this.addonsDeposit, delta });
        }
        await this.fetchAddons();
      } catch (error) {
        this.notifyError(error, this.$t("booking_addons.save_error"));
      } finally {
        this.busy = false;
      }
    },
    availableFor(addon) {
      if (addon.available === null || addon.available === undefined) return null;
      return addon.available;
    },
    // Суффикс у поля qty: «из N доступных» (услуги и невыбранные даты — пусто)
    stockSuffix(addon) {
      if (!addon.track_availability) return "";
      const available = this.availableFor(addon);
      if (available === null) return "";
      return this.$t("booking_addons.of_available", { count: available });
    },
    onQuantityChange(addon, rawValue) {
      const qty = Math.max(0, parseInt(rawValue, 10) || 0);
      if (this.live) {
        this.applyLiveChange(addon, qty);
      } else {
        // draft: только локальная проверка остатка (сервер перепроверит при постинге)
        if (addon.track_availability && qty > (addon.available || 0)) {
          this.notifyUnavailable(addon);
          this.$set(this.selection, addon.id, 0);
          return;
        }
        this.$set(this.selection, addon.id, qty);
      }
    },
    async applyLiveChange(addon, qty) {
      // Обнуление количества УДАЛЯЕТ строку, а повторное добавление соберёт её
      // заново из каталога — договорная цена потеряется (находка ревью).
      if (qty === 0 && addon.line_id && (addon.price_overridden || addon.deposit_overridden)) {
        const confirmed = await this.$confirm({
          title: this.$t("custom.you_sure_title"),
          text: this.$t("booking_addons.drop_overridden_warning"),
          icon: "mdi-alert-outline",
          iconColor: "warning",
          iconSize: 72,
          iconPosition: "top",
          confirmButtonText: this.$t("custom.yes"),
          confirmButtonColor: "primary",
          cancelButtonText: this.$t("custom.no"),
          cancelButtonColor: "grey darken-1",
          useFilledButtons: true,
          maxWidth: 450,
        });
        if (!confirmed) {
          // Ревью round 4 (п.1, фикс): поле количества — v-text-field
          // :value="quantityFor(addon)". Vuetify синхронизирует своё
          // внутреннее lazyValue (то, что реально рисуется в <input>) с
          // пропом value ЧЕРЕЗ WATCHER на этот проп, а watcher срабатывает,
          // только когда значение проп РЕАЛЬНО меняется. При отказе
          // количество в selection не менялось (было 3 — осталось 3), значит
          // :value до и после отказа резолвится в одно и то же число — Vue
          // не видит изменения пропа, watcher внутри VTextField молчит, и в
          // DOM остаётся набранный менеджером "0". Пересоздание объекта
          // selection (было опробовано через await fetchAddons() — не
          // сработало) тут не помогает: значение ПРОПА не меняется, реф на
          // объект selection полю не передаётся напрямую. Хуже — повторный
          // ввод "0" не даст нового @change (нативный input уже содержит
          // "0", diff пуст), позицию нельзя удалить, пока не ввести другое
          // число. Рабочий приём — форсировать пересоздание самого поля:
          // :key завязан на declineEpoch[addon.id], который тут
          // увеличивается; Vue уничтожает старый экземпляр VTextField и
          // монтирует новый, который инициализирует lazyValue заново из
          // актуального quantityFor(addon).
          this.$set(this.declineEpoch, addon.id, (this.declineEpoch[addon.id] || 0) + 1);
          return;
        }
      }
      this.busy = true;
      const previous = this.quantityFor(addon);
      try {
        let response = null;
        if (qty === 0 && addon.line_id) {
          response = await this.axios.delete(
            `/api/v1/bookings/${this.bookingId}/booking_store_products/${addon.line_id}`, this.authHeaders);
        } else if (addon.line_id) {
          response = await this.axios.patch(
            `/api/v1/bookings/${this.bookingId}/booking_store_products/${addon.line_id}`,
            { quantity: qty }, this.authHeaders);
        } else if (qty > 0) {
          // Ревью Task 7 (Important): подмешиваем отложенную цену/залог
          // (введённые до создания строки) в тело POST — иначе строка
          // создавалась бы по каталожной цене, а введённая договорная терялась.
          response = await this.axios.post(
            `/api/v1/bookings/${this.bookingId}/booking_store_products`,
            buildCreatePayload(addon.id, qty, this.pendingPricing[addon.id]), this.authHeaders);
          this.$delete(this.pendingPricing, addon.id);
        }
        const depositDelta = (qty - previous) * effectiveDeposit(addon);
        this.$set(this.selection, addon.id, qty);
        if (depositDelta !== 0) {
          this.$emit("addons-deposit-changed", { value: this.addonsDeposit, delta: depositDelta });
        }
        if (response && response.data && response.data.addons_cost !== undefined) {
          this.$emit("server-addons-cost", {
            addons_cost: response.data.addons_cost,
            booking_total: response.data.booking_total,
          });
        }
        await this.fetchAddons();
      } catch (error) {
        this.$set(this.selection, addon.id, previous);
        if (error.response && error.response.status === 422 && error.response.data.available !== undefined) {
          this.notifyUnavailable(addon, error.response.data.available);
        } else {
          this.notifyError(error, this.$t("booking_addons.save_error"));
        }
      } finally {
        this.busy = false;
      }
    },
    // NewBooking (draft): вызвать после создания брони — постим выбранные строки
    async postDraft(bookingId) {
      const entries = Object.entries(this.selection).filter(([, qty]) => qty > 0);
      const failures = [];
      for (const [addonId, qty] of entries) {
        const id = parseInt(addonId, 10);
        try {
          // Task 8: та же buildCreatePayload, что и в applyLiveChange —
          // подмешивает договорную цену/залог, введённые до создания брони
          // (pendingPricing), иначе строка создалась бы по каталожной цене.
          await this.axios.post(`/api/v1/bookings/${bookingId}/booking_store_products`,
            buildCreatePayload(id, qty, this.pendingPricing[addonId]), this.authHeaders);
          this.$delete(this.pendingPricing, addonId);
        } catch (error) {
          const addon = this.addons.find((a) => a.id === id);
          failures.push(addon ? addon.name : addonId);
        }
      }
      if (failures.length > 0) {
        this.$notify(this.$t("booking_addons.draft_post_failed", { names: failures.join(", ") }), "", "warning", 6000);
      }
      return failures;
    },
    notifyUnavailable(addon, available) {
      this.$notify(
        this.$t("booking_addons.unavailable", { name: addon.name, available: available !== undefined ? available : addon.available }),
        "", "warning", 5000);
    },
    notifyError(error, message) {
      console.warn(error);
      this.$notify(message, (error.response && error.response.data && error.response.data.error) || "", "error", 5000);
    },
  },
};
</script>
